import React, { useEffect, useRef, useState } from 'react';
import styles from "./aboutusSec.module.css";
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { motion } from 'framer-motion';
import useTextAnimation from '../../hooks/useTextAnimation';
import { aboutUsBg } from '../../../assets';
import '../../../i18n';
import { LANG_EN } from '../../helpers/constant';
import { useTranslation } from 'react-i18next';

gsap.registerPlugin(ScrollTrigger);

const AboutBanner = ({ configData }) => {
    const bannerRef = useRef(null);
    const bannerInnerRef = useRef(null);
    const titleParaRef = useRef(null);
    const innerBgContainerRef = useRef(null);
    const innerBgRef = useRef(null);
    const animationRef = useRef(null);
    const resizeTimeoutRef = useRef(null);

    const { t, i18n } = useTranslation();
    const { ref: abtBTRef, inView: abtBTInView, textAnimation } = useTextAnimation();
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            // Debounce resize events for better performance
            clearTimeout(resizeTimeoutRef.current);
            resizeTimeoutRef.current = setTimeout(() => {
                setIsMobile(window.innerWidth < 768);
                setupAnimations();
            }, 100);
        };

        const setupAnimations = () => {
            // Clean up previous animations
            if (animationRef.current) {
                animationRef.current.kill();
                ScrollTrigger.getAll().forEach(trigger => trigger.kill());
            }

            const bannerInner = bannerInnerRef.current;
            const titlePara = titleParaRef.current;
            const innerBg = innerBgRef.current;
            const innerBgContainer = innerBgContainerRef.current;

            // Set initial styles
            gsap.set(innerBgContainer, { y: 0, clearProps: "transform" });
            gsap.set(innerBg, { opacity: 1 });
            gsap.set(bannerInner, { opacity: 1, y: 0 });
            gsap.set(titlePara, { opacity: 0 });

            // Skip complex animations on mobile if low performance
            const isLowPerformance = isMobile && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

            if (isLowPerformance) {
                gsap.set([bannerInner, titlePara], { opacity: 1 });
                return;
            }

            // Animation parameters based on screen size
            const animationParams = {
                pin: !isMobile,
                scrub: isMobile ? 0.5 : 1.5,
                yOffset: isMobile ? -30 : -50,
                opacityEnd: isMobile ? 0.7 : 0.46,
                startTrigger: isMobile ? "20% center" : "40% center",
                endTrigger: isMobile ? "40% center" : "60% center"
            };

            // Create timeline for better animation control
            animationRef.current = gsap.timeline({
                scrollTrigger: {
                    trigger: bannerRef.current,
                    start: "top top",
                    end: "bottom bottom",
                    scrub: animationParams.scrub,
                    invalidateOnRefresh: true
                }
            });

            // Pin background (desktop only)
            if (animationParams.pin) {
                ScrollTrigger.create({
                    trigger: innerBgContainer,
                    start: "top top",
                    end: "bottom center",
                    pin: true,
                    scrub: animationParams.scrub,
                });
            }

            // Banner inner animation
            animationRef.current.to(bannerInner, {
                opacity: 0,
                y: animationParams.yOffset,
                ease: "power3.out"
            }, 0);

            // Title paragraph animation
            animationRef.current.to(titlePara, {
                opacity: 1,
                ease: "power3.out",
                onStart: () => {
                    gsap.to(innerBg, {
                        opacity: animationParams.opacityEnd,
                        duration: 1.5,
                        ease: "power2.out"
                    });
                },
                onReverseComplete: () => {
                    gsap.to(innerBg, {
                        opacity: 1,
                        duration: 1.5,
                        ease: "power2.out"
                    });
                }
            }, 0.3);

            ScrollTrigger.refresh();
        };

        // Initial setup
        setIsMobile(window.innerWidth < 768);
        setupAnimations();

        // Event listeners
        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(resizeTimeoutRef.current);
            if (animationRef.current) animationRef.current.kill();
            ScrollTrigger.getAll().forEach(trigger => trigger.kill());
        };
    }, [isMobile]);

    return (
        <div className={styles._inner} ref={bannerRef}>
            {/* Background container stays pinned */}
            <div className={styles._innerBgP} ref={innerBgContainerRef}>
                <span className={styles._innerBg} ref={innerBgRef}>
                    <img src={aboutUsBg} alt="" />
                </span>
            </div>
            <div className={styles._banner}>
                <div className={styles._bannerInner} ref={bannerInnerRef}>
                    <motion.h1
                        ref={abtBTRef}
                        initial="hidden"
                        animate={abtBTInView ? "visible" : "hidden"}
                        custom={0}
                        variants={textAnimation}
                        className={styles._title}
                    >
                        {t('aboutBannerOur')} <strong>{t('aboutBannerPurpose')}</strong> {t('aboutBannerCreate')}
                    </motion.h1>
                    <motion.h3
                        ref={abtBTRef}
                        initial="hidden"
                        animate={abtBTInView ? "visible" : "hidden"}
                        custom={0}
                        variants={textAnimation}
                        className={styles._titleSub}
                    >
                        {t('aboutTrulyHumans')}
                    </motion.h3>
                </div>
                <motion.p
                    ref={titleParaRef}
                    className={styles._titlePara}
                    dangerouslySetInnerHTML={{ __html: t('aboutBannerDescription') }}
                />
            </div>
        </div>
    );
};

export default AboutBanner;
